/**
 * API for subscription plan config so Admin and Petso share one source of truth.
 * Backend should expose GET /subscription-config and PUT /subscription-config.
 * If the backend does not yet implement these, requests will 404 and the app
 * falls back to localStorage / INITIAL config.
 */

import { apiClient } from '@/api/axios';
import type { ProductConfig } from '@shared/subscriptionConfig';

const SUBSCRIPTION_CONFIG_PATH = '/subscription-config';

/**
 * Fetch subscription products from the backend. Returns null on 404 or error
 * so callers can fall back to localStorage or INITIAL.
 */
export async function fetchSubscriptionConfig(): Promise<
  ProductConfig[] | null
> {
  try {
    const { data } = await apiClient.get<ProductConfig[]>(
      SUBSCRIPTION_CONFIG_PATH
    );
    if (Array.isArray(data) && data.length > 0) return data;
    return null;
  } catch {
    // 404 or network error: backend may not have the endpoint yet
    return null;
  }
}

/**
 * Persist subscription config to the backend (Admin only).
 * No-op on failure so Admin can still use localStorage.
 */
export async function saveSubscriptionConfigToApi(
  products: ProductConfig[]
): Promise<boolean> {
  try {
    await apiClient.put(SUBSCRIPTION_CONFIG_PATH, products);
    return true;
  } catch {
    return false;
  }
}
